import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  ExternalLink,
  BookOpen,
  FileText,
  Wrench,
  Sparkles,
  User,
  Zap,
  Gem
} from 'lucide-react';
import { useAuthGuard } from '../hooks/useAuthGuard';
import LoginModal from './LoginModal';

// AI工具配置 - 参考AI Fleet的设计风格
const AI_TOOLS = [
  {
    id: 'google-scholar',
    name: 'Google Scholar',
    nameZh: '谷歌学术',
    description: '搜索学术文献、论文、引文、书籍、会议论文、学位论文等学术资源',
    url: 'https://scholar.google.com',
    internalRoute: '/google-scholar/sidebar',
    icon: BookOpen,
    gradient: 'from-blue-500 to-blue-600',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    image: '/images/google-scholar.jpg', // 可以添加工具的logo图片
    costKind: 'free'
  },
  {
    id: 'aippt',
    name: 'AI-PPT',
    nameZh: 'AI-PPT',
    description: '使用AI技术快速生成专业演示文稿，支持多种模板和自动排版',
    url: 'https://www.aippt.cn',
    internalRoute: '/ai-toolbox/sidebar/aippt',
    icon: FileText,
    gradient: 'from-green-500 to-green-600',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-200',
    image: '/images/aippt-placeholder.svg',
    statusLabel: '内测中',
    costKind: 'diamond',
    costAmount: 99
  }
];

export default function AIToolboxPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { isLoggedIn, showLoginModal, requireAuth, closeLoginModal, handleLoginSuccess } = useAuthGuard();
  const [selectedTool, setSelectedTool] = useState(null);

  // 处理工具点击
  const handleToolClick = (tool) => {
    if (tool.disabled) {
      return;
    }

    if (!requireAuth()) {
      return;
    }

    setSelectedTool(tool);

    if (tool.internalRoute) {
      navigate(tool.internalRoute);
    } else {
      window.open(tool.url, '_blank', 'noopener,noreferrer');
    }

    console.log(`用户访问了工具: ${tool.name}`);
  };

  return (
    <>
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 xl:px-8 py-4 sm:py-6 lg:py-8">
        
        {/* 页面标题 - 简洁版 */}
        <div className="text-center mb-6 sm:mb-8">
          <div className="flex items-center justify-center gap-3 mb-3">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <Wrench className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{t('sidebar.aiFeatures.aiToolbox')}</h1>
          </div>
          <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
            {t('aiToolbox.description', '发现和使用各种AI工具，提升工作效率，激发创意潜能')}
          </p>
        </div>

        {/* AI工具网格 - 完全参考AI Fleet车队卡片格式 */}
        <div className="flex justify-center">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8 mb-6 sm:mb-8 max-w-2xl">
          {AI_TOOLS.map((tool, index) => {
            const IconComponent = tool.icon;
            const isSelected = selectedTool && selectedTool.id === tool.id;
            const toolName = i18n.language.startsWith('zh') ? tool.nameZh : tool.name;

            // 为每个工具类型固定配色方案 - 参考车队配色
            const toolColorMap = {
              'google-scholar': {
                third: 'from-blue-200 to-purple-300',
                second: 'from-green-200 to-teal-300'
              },
              'aippt': {
                third: 'from-green-200 to-emerald-300',
                second: 'from-yellow-200 to-green-300'
              }
            };

            const colors = toolColorMap[tool.id] || toolColorMap['google-scholar'];

            return (
              <div
                key={tool.id}
                className={`bg-white rounded-xl sm:rounded-2xl overflow-hidden shadow-md hover:shadow-xl transform hover:scale-105 transition-all duration-300 border-2 group ${
                  tool.disabled ? 'opacity-75 cursor-not-allowed' : 'cursor-pointer'
                } ${
                  isSelected ? 'border-purple-500 ring-2 ring-purple-200' : 'border-gray-100'
                }`}
                onClick={() => !tool.disabled && handleToolClick(tool)}
              >
                {/* 工具图标区域 - 正方形 */}
                <div className="bg-white aspect-square relative overflow-hidden">
                  <div className="relative h-full flex items-center justify-center p-3 sm:p-4">
                    {/* 堆叠卡片背景装饰 */}
                    <div className="absolute inset-0 flex items-center justify-center">
                      {/* 第三层背景卡片 */}
                      <div className={`absolute w-4/5 h-4/5 bg-gradient-to-br ${colors.third} rounded-xl transform rotate-6 group-hover:rotate-12 transition-transform duration-500 opacity-60`}></div>
                      {/* 第二层背景卡片 */}
                      <div className={`absolute w-4/5 h-4/5 bg-gradient-to-br ${colors.second} rounded-xl transform -rotate-3 group-hover:-rotate-6 transition-transform duration-500 opacity-80`}></div>
                    </div>
                    
                    {/* 主工具图片容器 */}
                    <div className="relative z-10 w-4/5 h-4/5">
                      <div className="w-full h-full rounded-xl overflow-hidden group-hover:scale-105 transition-all duration-500 shadow-xl">
                        <img 
                          src={tool.image}
                          alt={toolName}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            // 如果图片加载失败，显示渐变背景和图标
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                        
                        {/* 备用图标显示 */}
                        <div 
                          className={`w-full h-full bg-gradient-to-br ${tool.gradient} rounded-xl flex items-center justify-center`}
                          style={{display: 'none'}}
                        >
                          <IconComponent className="w-12 h-12 text-white" />
                        </div>
                      </div>
                      
                      {/* 图片上的光泽效果 */}
                      <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/20 to-transparent rounded-xl"></div>
                    </div>
                  </div>
                  
                  {/* 状态标签 - 显示"免费"、"热门"或"开发中" */}
                  <div className={`absolute top-2 right-2 px-2 py-1 rounded-full text-xs font-bold z-20 flex items-center gap-1 ${
                    tool.disabled 
                      ? 'bg-gray-100 text-gray-600' 
                      : 'bg-green-100 text-green-700'
                  }`}>
                    <Sparkles className="w-2.5 h-2.5" />
                    <span>{tool.disabled ? '开发中' : (tool.statusLabel || '免费')}</span>
                  </div>
                </div>
                
                {/* 工具信息区域 */}
                <div className="p-3 sm:p-4">
                  {/* 工具名称 */}
                  <h3 className="text-sm sm:text-base font-bold text-gray-900 mb-1 group-hover:text-purple-600 transition-colors duration-300 text-center">
                    {toolName}
                  </h3>
                  
                  {/* 工具简介 */}
                  <p className="text-xs sm:text-sm text-gray-600 text-center mb-3 sm:mb-4 leading-relaxed line-clamp-2">
                    {tool.description.length > 40 ? `${tool.description.substring(0, 40)}...` : tool.description}
                  </p>
                  
                  {/* 立即使用按钮 */}
                  {/* 费用说明 */}
                  <div className="mb-3 sm:mb-4">
                    {tool.costKind === 'free' && (
                      <div className="flex items-center justify-center gap-2 text-xs sm:text-sm">
                        <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium">{t('aiToolbox.cost.free', '免费')}</span>
                        <span className="text-gray-500">{t('aiToolbox.cost.freeHint', '站点用户可免费使用')}</span>
                      </div>
                    )}
                    {tool.costKind === 'diamond' && (
                      <div className="flex items-center justify-center gap-2 text-xs sm:text-sm">
                        <span className="px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 font-medium flex items-center gap-1">
                          <Gem className="w-3.5 h-3.5" />
                          {t('aiToolbox.cost.perUse', '{{amount}} 钻/次').replace('{{amount}}', tool.costAmount)}
                        </span>
                        <span className="text-gray-500">{t('aiToolbox.cost.aipptCostHint', '每次使用消耗99个钻石').replace('{{amount}}', tool.costAmount)}</span>
                      </div>
                    )}
                  </div>

                  {/* 立即使用按钮 */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!tool.disabled) {
                        handleToolClick(tool);
                      }
                    }}
                    disabled={tool.disabled}
                    className={`w-full py-1.5 sm:py-2 rounded-md sm:rounded-lg font-medium text-xs sm:text-sm transition-all duration-300 border ${
                      tool.disabled
                        ? 'border-gray-300 text-gray-500 bg-gray-50 cursor-not-allowed'
                        : 'border-purple-300 text-purple-600 bg-white hover:bg-purple-50 hover:border-purple-400 hover:shadow-md hover:text-purple-700'
                    }`}
                  >
                    {tool.disabled ? tool.disabledText : t('aiToolbox.useNow', '立即使用')}
                  </button>
                </div>
              </div>
            );
          })}
          </div>
        </div>

        {/* 已选工具详情 */}
        {selectedTool && (
          <div className="bg-gradient-to-r from-purple-50 via-yellow-50 to-purple-50 border border-purple-200 rounded-2xl sm:rounded-3xl mb-6 sm:mb-8 shadow-sm">
            <div className="px-4 sm:px-6 lg:px-8 py-4 sm:py-5">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
                {/* 左侧：工具详细信息 */}
                <div className="flex items-center gap-3 sm:gap-4">
                  {/* 工具图标 */}
                  <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-xl overflow-hidden shadow-md bg-white p-1">
                    <img 
                      src={selectedTool.image}
                      alt={selectedTool.name}
                      className="w-full h-full rounded-lg object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div 
                      className={`w-full h-full bg-gradient-to-br ${selectedTool.gradient} rounded-lg flex items-center justify-center`}
                      style={{display: 'none'}}
                    >
                      <selectedTool.icon className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
                    </div>
                  </div>
                  
                  {/* 工具信息 */}
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-lg sm:text-xl font-bold text-gray-900">
                        {i18n.language.startsWith('zh') ? selectedTool.nameZh : selectedTool.name}
                      </h3>
                      <ExternalLink className="w-4 h-4 text-gray-500" />
                    </div>
                    <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                      {selectedTool.description}
                    </p>
                    {/* 费用说明（详情区） */}
                    <div className="mt-2 flex items-center gap-2">
                      {selectedTool.costKind === 'free' && (
                        <>
                          <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-medium">{t('aiToolbox.cost.free', '免费')}</span>
                          <span className="text-xs text-gray-500">{t('aiToolbox.cost.freeHint', '站点用户可免费使用')}</span>
                        </>
                      )}
                      {selectedTool.costKind === 'diamond' && (
                        <>
                          <span className="px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 text-xs font-medium inline-flex items-center gap-1">
                            <Gem className="w-3.5 h-3.5" />
                            {t('aiToolbox.cost.perUse', '{{amount}} 钻/次').replace('{{amount}}', selectedTool.costAmount)}
                          </span>
                          <span className="text-xs text-gray-500">{t('aiToolbox.cost.aipptCostHint', '每次使用消耗99个钻石').replace('{{amount}}', selectedTool.costAmount)}</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* 右侧：访问按钮 */}
                <div className="flex gap-3">
                  <button
                    onClick={() => !selectedTool.disabled && handleToolClick(selectedTool)}
                    disabled={selectedTool.disabled}
                    className={`px-4 sm:px-6 py-2 sm:py-3 rounded-lg font-medium transition-colors duration-300 flex items-center gap-2 ${
                      selectedTool.disabled
                        ? 'bg-gray-400 text-white cursor-not-allowed'
                        : 'bg-purple-500 text-white hover:bg-purple-600'
                    }`}
                  >
                    <span>{selectedTool.disabled ? selectedTool.disabledText : t('common.visit', '访问工具')}</span>
                    {!selectedTool.disabled && <ExternalLink className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 底部提示信息 - 简化版 */}
        <div className="text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-full text-sm text-gray-600">
            <Sparkles className="w-4 h-4 text-purple-500" />
            <span>{t('aiToolbox.stats.totalTools', { count: AI_TOOLS.length }, `共 ${AI_TOOLS.length} 个工具`)}</span>
          </div>
        </div>
      </div>
    </div>

    <LoginModal
      isOpen={showLoginModal}
      onClose={closeLoginModal}
      onLogin={handleLoginSuccess}
    />
    </>
  );
}
